import { api } from './api'
import { isNative } from './config'

const NATIVE_MARKER = 'fn_native_push'

/**
 * Web-Push-Opt-in (10 – UI/UX). iOS verlangt: installierte PWA + Nutzergeste.
 * Der Aufruf von enablePush() muss aus einem Klick-Handler kommen.
 * Im nativen Build (Capacitor) läuft stattdessen FCM (ADR-042 §3).
 */
export function pushSupported(): boolean {
  if (isNative) return true
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

export function pushPermission(): NotificationPermission | 'unsupported' {
  if (isNative) return localStorage.getItem(NATIVE_MARKER) === '1' ? 'granted' : 'default'
  if (!pushSupported()) return 'unsupported'
  return Notification.permission
}

function nativePlatform(): 'android' | 'ios' {
  return window.Capacitor?.getPlatform?.() === 'ios' ? 'ios' : 'android'
}

let nativeListenersReady = false

/**
 * Native (Capacitor): einmalig Listener registrieren — FCM-Token an den Server
 * schicken (auch bei Rotation) und Tap auf eine Benachrichtigung behandeln.
 * Wenn die Erlaubnis schon erteilt ist, wird direkt (neu) registriert.
 */
export async function initNativePush(onOpenUrl?: (url: string) => void): Promise<void> {
  if (!isNative || nativeListenersReady) return
  nativeListenersReady = true
  const { PushNotifications } = await import('@capacitor/push-notifications')
  await PushNotifications.addListener('registration', (token) => {
    void api
      .pushRegisterNative({ token: token.value, platform: nativePlatform(), deviceLabel: navigator.userAgent.slice(0, 100) })
      .catch(() => {})
  })
  await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const url = (action.notification.data as { url?: string } | undefined)?.url
    if (url && onOpenUrl) onOpenUrl(url)
  })
  const perm = await PushNotifications.checkPermissions()
  if (perm.receive === 'granted') {
    localStorage.setItem(NATIVE_MARKER, '1')
    await PushNotifications.register()
  }
}

async function enableNativePush(): Promise<EnableResult> {
  try {
    await initNativePush()
    const { PushNotifications } = await import('@capacitor/push-notifications')
    let perm = await PushNotifications.checkPermissions()
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions()
    }
    if (perm.receive !== 'granted') return 'denied'
    localStorage.setItem(NATIVE_MARKER, '1')
    await PushNotifications.register() // Token kommt über den 'registration'-Listener
    return 'enabled'
  } catch {
    return 'error'
  }
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(b64)
  const arr = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i)
  return arr
}

export type EnableResult = 'enabled' | 'denied' | 'unsupported' | 'no-key' | 'error'

/** Berechtigung anfragen und Push-Subscription am Server registrieren. */
export async function enablePush(): Promise<EnableResult> {
  if (isNative) return enableNativePush()
  if (!pushSupported()) return 'unsupported'
  try {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return 'denied'

    const key = await api.pushVapidKey()
    if (!key) return 'no-key'

    const reg = await navigator.serviceWorker.ready
    let sub = await reg.pushManager.getSubscription()
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
      })
    }
    const json = sub.toJSON()
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return 'error'
    await api.pushSubscribe({
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      deviceLabel: navigator.userAgent.slice(0, 100),
    })
    return 'enabled'
  } catch {
    return 'error'
  }
}

/** Push auf diesem Gerät abschalten. */
export async function disablePush(): Promise<void> {
  if (isNative) {
    localStorage.removeItem(NATIVE_MARKER)
    return
  }
  if (!pushSupported()) return
  const reg = await navigator.serviceWorker.ready
  const sub = await reg.pushManager.getSubscription()
  if (sub) {
    await api.pushUnsubscribe(sub.endpoint).catch(() => {})
    await sub.unsubscribe().catch(() => {})
  }
}
